import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
    getComplains,
    markAllComplainsSeen,
    markComplainSeen,
    removeComplains,
} from "services/complains";

/**
 * The complaints screen's data.
 *
 * WHY REACT QUERY AND NOT THE OLD HOOK. `useComplain` fetched into Redux and
 * the screen kept its own `useState` copy on top, so three things held the same
 * list and each write had to patch all of them by hand. It also could not touch
 * the SIDEBAR badge, which is a `useQuery` of its own — so marking a complaint
 * read left the badge showing the old count until its 30-second poll came
 * round, and an operator who had just cleared the queue still saw «جديدة 2».
 *
 * Every mutation here invalidates both keys, so the list and the badge move
 * together.
 */
export const complaintKeys = {
    all: ["complaints"],
    list: (params) => ["complaints", params],
    /** Owned by the sidebar (`SideBar.js`), invalidated from here. */
    unseenBadge: ["sidebar-unseen-complaints"],
};

export const useComplaints = (params = { page: 1, size: 100 }) => {
    const query = useQuery({
        queryKey: complaintKeys.list(params),
        queryFn: () => getComplains(params),
    });

    return {
        complaints: query.data?.result ?? [],
        count: query.data?.count ?? 0,
        loading: query.isLoading,
        error: query.error?.response?.data?.message ?? null,
    };
};

/**
 * Invalidate the list and the sidebar badge together.
 *
 * `refetchType: "all"` on the badge because the sidebar query is mounted on
 * every screen; without it an inactive query is only marked stale and the badge
 * would still wait for its poll.
 */
const useComplaintMutation = (mutationFn) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn,
        onSuccess: async () => {
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: complaintKeys.all }),
                queryClient.invalidateQueries({
                    queryKey: complaintKeys.unseenBadge,
                    refetchType: "all",
                }),
            ]);
        },
    });
};

export const useMarkComplaintSeen = () => useComplaintMutation(markComplainSeen);
export const useMarkAllComplaintsSeen = () => useComplaintMutation(markAllComplainsSeen);
export const useDeleteComplaint = () => useComplaintMutation(removeComplains);
